import React, { useCallback, useEffect, useMemo, useState } from "react";
import TopBar from "@/components/TopBar";
import { listSimulations, deleteSimulation } from "@/lib/api";
import { useI18n } from "@/context/LanguageContext";
import { useSimulation } from "@/context/SimulationContext";
import { useNavigate, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Trash2, ArrowUpRight, Building2, Search, Plus, Copy } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { computeSavings, defaultTrailer, defaultCustomer, defaultPrimo, defaultClient } from "@/lib/simulation-defaults";
import { toast } from "sonner";

export default function SavedSimulationsPage() {
  const { t } = useI18n();
  const { loadSimulation, duplicateSimulation } = useSimulation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listSimulations();
      setItems(data);
    } catch (e) {
      console.error(e);
      toast.error(t("common.error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (s) =>
        (s.client?.company_name || "").toLowerCase().includes(q) ||
        (s.client?.contact_name || "").toLowerCase().includes(q) ||
        (s.client?.contact_email || "").toLowerCase().includes(q)
    );
  }, [items, query]);

  const handleOpen = (sim) => {
    loadSimulation(sim);
    navigate("/simulator");
  };

  const handleDuplicate = (sim) => {
    // Loads the sim as a brand-new editable draft (no server id) and jumps
    // the wizard to Phase 1 so the salesperson can adjust the client info
    // before saving. The company name is auto-suffixed with the localized
    // copy tag (e.g. "(Cópia)" / "(Copy)") to keep the two entries distinct.
    duplicateSimulation(sim, t("saved.copySuffix"));
    toast.success(t("common.duplicated"));
    navigate("/simulator");
  };

  const handleDelete = async (id) => {
    try {
      await deleteSimulation(id);
      toast.success(t("common.deleted"));
      setItems((arr) => arr.filter((s) => s.id !== id));
    } catch (e) {
      toast.error(t("common.error"));
    }
  };

  return (
    <div className="min-h-screen">
      <TopBar />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="w-8 h-px bg-[color:var(--brand)]" />
              <span className="text-xs font-mono uppercase tracking-[0.22em] text-[color:var(--brand)]">Archive</span>
            </div>
            <h1 className="font-display font-bold text-[color:var(--text)] text-4xl sm:text-5xl tracking-tight">
              {t("saved.title")}
            </h1>
            <p className="text-[color:var(--text-dim)] mt-3 text-base">{t("saved.subtitle")}</p>
          </div>
          <Link
            to="/simulator"
            data-testid="new-sim-btn"
            className="inline-flex items-center gap-2 bg-[color:var(--brand)] hover:bg-[color:var(--brand-dim)] text-black font-semibold h-12 px-5 rounded transition-colors"
          >
            <Plus className="w-4 h-4" />
            {t("saved.newBtn")}
          </Link>
        </div>

        <div className="mb-6 relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-mute)]" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("saved.search")}
            data-testid="search-input"
            className="pl-10 h-12 bg-[color:var(--surface)] border-[color:var(--border)] text-[color:var(--text)] font-body focus-visible:ring-[color:var(--brand)]"
          />
        </div>

        {loading ? (
          <div className="text-[color:var(--text-dim)] font-mono py-16 text-center" data-testid="loading-state">
            {t("common.loading")}
          </div>
        ) : filtered.length === 0 ? (
          <div className="card-tech p-16 text-center" data-testid="empty-state">
            <Building2 className="w-8 h-8 text-[color:var(--text-mute)] mx-auto mb-4" />
            <p className="text-[color:var(--text-dim)]">{t("saved.empty")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filtered.map((s) => {
              const merged = {
                client: { ...defaultClient, ...(s.client || {}) },
                trailer: { ...defaultTrailer, ...(s.trailer || {}) },
                customer: { ...defaultCustomer, ...(s.customer || {}) },
                primo: { ...defaultPrimo, ...(s.primo || {}) },
              };
              const savings = computeSavings(merged).priceSavings;
              const created = s.created_at ? new Date(s.created_at).toLocaleDateString() : "";
              return (
                <div
                  key={s.id}
                  data-testid={`sim-card-${s.id}`}
                  className="card-tech card-tech-hover p-6 group"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-mute)] mb-1">
                        {t("common.created")}: {created}
                      </div>
                      <div className="font-display font-bold text-white text-xl truncate" data-testid={`sim-name-${s.id}`}>
                        {s.client?.company_name || "—"}
                      </div>
                      {s.client?.contact_name && (
                        <div className="text-sm text-[color:var(--text-dim)] truncate">{s.client.contact_name}</div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-mute)] mb-1">
                        {t("reveal.pricePerSet")}
                      </div>
                      <div className="font-display font-bold text-[color:var(--brand)] text-2xl">
                        € {savings.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 flex items-center gap-2">
                    <Button
                      onClick={() => handleOpen(s)}
                      data-testid={`open-sim-${s.id}`}
                      className="bg-[color:var(--surface-2)] hover:bg-[color:var(--brand)] hover:text-white text-[color:var(--text)] border border-[color:var(--border-strong)] hover:border-[color:var(--brand)] h-10 rounded transition-colors"
                    >
                      <ArrowUpRight className="w-4 h-4 mr-1" />
                      {t("saved.open")}
                    </Button>
                    <Button
                      onClick={() => handleDuplicate(s)}
                      data-testid={`duplicate-sim-${s.id}`}
                      className="bg-transparent hover:bg-[color:var(--surface-2)] text-[color:var(--text)] border border-[color:var(--border-strong)] hover:border-[color:var(--brand)] hover:text-[color:var(--brand)] h-10 rounded transition-colors"
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      {t("saved.duplicate")}
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button
                          data-testid={`delete-sim-${s.id}`}
                          className="p-2 border border-[color:var(--border)] hover:border-[color:var(--danger)] hover:text-[color:var(--danger)] text-[color:var(--text-dim)] rounded transition-colors"
                          aria-label={t("common.delete")}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-[color:var(--surface)] border-[color:var(--border)] text-[color:var(--text)]">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-[color:var(--text)]">{t("saved.confirmDelete")}</AlertDialogTitle>
                          <AlertDialogDescription className="text-[color:var(--text-dim)]">
                            {t("saved.deleteWarning")}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="bg-transparent border-[color:var(--border-strong)] text-[color:var(--text)] hover:bg-[color:var(--surface-2)] hover:text-[color:var(--text)]">
                            {t("common.cancel")}
                          </AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(s.id)}
                            data-testid={`confirm-delete-${s.id}`}
                            className="bg-[color:var(--danger)] hover:bg-red-700 text-white"
                          >
                            {t("common.delete")}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
